import { CloseCircleIcon } from '@velocity/icons/system';
import ls from 'localstorage-slim';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { cancelLeaseApplication } from '../../../api/instant-lease-api';
import {
  setIsAnimated,
  setNotification,
  setSubheaderVisibility,
} from '../../../redux/cache-slice';
import { fetchLeaseApp } from '../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { Status, Type } from '../../../types/instant-lease-api';
import {
  formatGermanPrice,
  getIsAllowedToBeCanceled,
  getText,
} from '../../../utils/getter';
import { ReactComponent as ArrowRightSm } from '../../b2b/components/icons/chevron-right-sm.svg';
import Modal from '../../b2b/components/modal/modal';
import ButtonGroup from '../../b2b/molecules/button-group/button-group';
import { ReactComponent as CarFront } from '../icons/car-front-24.svg';
import { ReactComponent as ChevronDownSm } from '../icons/chevron-down-sm.svg';
import VehicleOverview from '../vehicle-overview/vehicle-overview';
import SubHeaderBand from './sub-header-band';

const toggleFocusRing =
  'focus:outline-none focus-visible:shadow-[0_0_0_2px_#0065ff]';

const SubHeader: React.FC<{
  forceEnable?: boolean;
  showCancelButton?: boolean;
}> = ({ forceEnable, showCancelButton }) => {
  // Lease Applications Store
  const { carData, activeApplication, leaseApplications } = useSelector(
    (state: RootState) => state.leaseApp,
  );
  // Lease Application Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // History hook
  const navigate = useNavigate();

  // pathname
  const { pathname } = useLocation();

  // Cache Store
  const { isSubheaderVisible, isAnimated } = useSelector(
    (state: RootState) => state.cache,
  );

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Subheader visibility
  const [priceChangeModalVisibility, setPriceChangeModalVisibility] =
    useState<boolean>(false);

  const subHeaderRef = useRef<HTMLDivElement>(null);

  // Variables
  const isCommercial = activeApplication?.type === Type.B2C;

  const vehicleData = activeApplication?.offer?.vehicle_data;

  const leasingOptions = activeApplication?.offer?.leasing_options;

  const vehicleRate = isCommercial
    ? leasingOptions?.totals?.rate?.gross
    : leasingOptions?.totals?.rate?.net;

  // Subheader Control
  const isSubheaderEnabled = !(
    (!carData && !vehicleData) ||
    activeApplication?.state === Status.CANCELLED
  );

  const toggleSubheader = () => {
    const subheaderElement = subHeaderRef.current;

    if (isSubheaderVisible) {
      if (subheaderElement) {
        // If the panel was left at `max-height: none`, restore a concrete value
        // before animating back to 0.
        if (subheaderElement.style.maxHeight === 'none') {
          subheaderElement.style.maxHeight = `${subheaderElement.scrollHeight}px`;
        }

        subheaderElement.style.maxHeight = `${subheaderElement.scrollHeight}px`;
        requestAnimationFrame(() => {
          subheaderElement.style.maxHeight = '0';
        });
      }

      setTimeout(() => {
        dispatch(setSubheaderVisibility(false));
      }, 500);
    } else {
      dispatch(setSubheaderVisibility(true));

      if (subheaderElement) {
        subheaderElement.style.maxHeight = '0';
        requestAnimationFrame(() => {
          subheaderElement.style.maxHeight = `${subheaderElement.scrollHeight}px`;
        });

        // Prevent clipping of bottom content (including close toggle) once open.
        setTimeout(() => {
          if (
            subHeaderRef.current &&
            subHeaderRef.current.style.maxHeight !== '0px'
          ) {
            subHeaderRef.current.style.maxHeight = 'none';
          }
        }, 10);
      }
    }
  };

  // handle price change modal
  useEffect(() => {
    // PCMS stand for price change modal status
    const pcms = ls.get('pcms');
    // If the price change again, the user will see the modal again
    const rateHasChanged = vehicleRate !== pcms;

    if (
      activeApplication &&
      carData?.rate !== undefined &&
      vehicleRate !== undefined &&
      carData?.rate !== vehicleRate &&
      carData?.carFullName === vehicleData?.full_name &&
      rateHasChanged &&
      isSubheaderEnabled
    ) {
      setPriceChangeModalVisibility(true);
    } else {
      setPriceChangeModalVisibility(false);
    }
  }, [
    carData?.rate,
    vehicleRate,
    activeApplication?.uuid,
    isSubheaderEnabled,
    leaseApplications.length,
    activeApplication,
    carData?.carFullName,
    vehicleData?.full_name,
  ]);

  useEffect(() => {
    const bootstrapPage = ls.get('bootstrapPage');

    if (pathname.includes('/login') && bootstrapPage) {
      //  Open immediately (no animation)
      dispatch(setIsAnimated(false));
      dispatch(setSubheaderVisibility(true));

      const el = subHeaderRef.current;
      if (!el) return;

      // Helper to close with smooth animation
      const closeSmoothly = () => {
        // Force the element to have its full height
        el.style.maxHeight = `${el.scrollHeight}px`;

        // Wait one frame so the browser registers the maxHeight
        requestAnimationFrame(() => {
          dispatch(setIsAnimated(true));
          window.scrollTo({ top: 0, behavior: 'smooth' });

          // Trigger the close animation
          el.style.transition =
            'max-height 0.5s ease-in-out, padding 0.5s ease-in-out';
          el.style.maxHeight = '0';
        });
      };

      // If images exist, wait for them to load
      const images = el.querySelectorAll('img');
      if (images.length === 0) {
        requestAnimationFrame(closeSmoothly);
      } else {
        let loadedCount = 0;
        images.forEach((img) => {
          if (img.complete) loadedCount++;
          else
            img.onload = () => {
              loadedCount++;
              if (loadedCount === images.length) closeSmoothly();
            };
        });
        if (loadedCount === images.length) requestAnimationFrame(closeSmoothly);
      }
    }

    let innerTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutId = setTimeout(
      () => {
        if (pathname.includes('/login')) {
          dispatch(setSubheaderVisibility(true));
          dispatch(setIsAnimated(false));

          innerTimeoutId = setTimeout(() => {
            dispatch(setSubheaderVisibility(false));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 400);
        }
      },
      isAnimated ? 0 : 3001,
    );

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(innerTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, dispatch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isSubheaderVisible) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      if (!isSubheaderVisible) {
        const activeSequenceList = document.querySelectorAll(
          '#sequence-steps [aria-current="step"]',
        );

        if (activeSequenceList.length > 0) {
          const activeSequence = activeSequenceList[0];
          activeSequence.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [isSubheaderVisible]);

  // Format price with german price format
  const { format } = formatGermanPrice();

  // Handle cancel leasing application
  const cancelLeaseApp = () => {
    if (getIsAllowedToBeCanceled(activeApplication?.state)) {
      setPriceChangeModalVisibility(false);
      cancelLeaseApplication(accessToken, activeApplication?.uuid as string)
        .then(() => {
          dispatch(
            fetchLeaseApp({
              accessToken,
              leaseAppId: activeApplication?.uuid as string,
            }),
          ).then(() => {
            ls.set('pcms', vehicleRate);
            navigate('/');
          });
        })
        .catch(() => {
          setPriceChangeModalVisibility(true);
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
        });
    }
  };
  // If subheader disabled or not forced
  if (!forceEnable && !isSubheaderEnabled) {
    return <div />;
  }

  return (
    <>
      <div className='relative'>
        <SubHeaderBand />
        {!isSubheaderVisible && (
          <div className='absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2'>
            <div
              className={`alt-text-tight-200-bold flex cursor-pointer items-center justify-center gap-1 overflow-clip rounded-3xl bg-(--white,#fff) px-4 py-1.5 whitespace-nowrap text-(--vel-color-secondary-400) shadow-[0_0_1px_0_rgba(9,30,66,0.2),0_2px_2px_0_rgba(9,30,66,0.08)] [&_svg]:size-6 [&_svg]:shrink-0 [&_svg_path]:fill-current ${toggleFocusRing}`}
              role='button'
              tabIndex={0}
              aria-expanded='false'
              aria-label='Mein Fahrzeug einblenden'
              data-testid='subheader-toggle'
              onClick={toggleSubheader}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSubheader();
                }
              }}
            >
              <CarFront aria-hidden='true' />
              <span>Mein Fahrzeug</span>
            </div>
          </div>
        )}
      </div>
      {isSubheaderVisible && (
        <div
          className='animate-fade-in absolute left-0 z-9 h-screen w-screen cursor-pointer bg-[rgba(15,53,73,0.5)]'
          aria-hidden='true'
          data-testid='subheader-scrim'
          onClick={toggleSubheader}
        />
      )}
      <div
        ref={subHeaderRef}
        className={`absolute left-0 z-10 max-h-0 w-screen overflow-hidden transition-[max-height,padding] duration-500 ease-in-out ${
          isSubheaderVisible ? 'pb-5' : ''
        }`}
        data-testid='subheader-drawer'
      >
        <div className={`relative ${isSubheaderVisible ? '' : 'hidden'}`}>
          <div className='bg-[color:var(--white,#fff)] px-4 pt-6 pb-12 shadow-[0_0_0.5px_0_rgba(9,30,66,0.2),0_2px_1px_0_rgba(9,30,66,0.08)] md:px-8 md:pt-8 md:pb-14 lg:pt-10'>
            <VehicleOverview showCancelButton={showCancelButton} />
          </div>
          {isSubheaderVisible && (
            <div className='absolute inset-x-0 -bottom-5 z-10 flex justify-center'>
              <div
                className={`alt-text-tight-200-bold flex cursor-pointer items-center justify-center gap-2 overflow-clip rounded-3xl bg-[#077] py-2 pr-3 pl-4 whitespace-nowrap text-(--white,#fff) shadow-[0_0_1px_0_rgba(9,30,66,0.2),0_2px_2px_0_rgba(9,30,66,0.08)] [&_svg]:m-0 [&_svg]:size-6 [&_svg]:shrink-0 [&_svg]:text-(--white,#fff) [&_svg_path]:fill-current ${toggleFocusRing}`}
                role='button'
                tabIndex={0}
                aria-expanded='true'
                aria-label='Mein Fahrzeug ausblenden'
                onClick={() => {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  toggleSubheader();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSubheader();
                  } else if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    toggleSubheader();
                  }
                }}
              >
                <CarFront aria-hidden='true' />
                <span>Mein Fahrzeug</span>
                <ChevronDownSm className='rotate-180' aria-hidden='true' />
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        type={device === 'mobile' ? 'top' : 'middle'}
        direction='down'
        isOpen={priceChangeModalVisibility}
        closeButtonVisibility={false}
      >
        <div className='flex flex-row gap-y-[30px]'>
          <h1 className='headline-300'>Änderung der Leasingrate</h1>
          <div className='flex'>
            <div>
              <ArrowRightSm />
            </div>
            <p
              className={`left text-loose-200 ${
                device === 'mobile' ? 'pt-1' : 'pt-0'
              }`}
            >
              Der Hersteller hat eine Preisänderung vorgenommen. Daher wurde
              dein Angebot neu berechnet und die auf der
              Fahrzeugkonfigurationsseite angegebene Leasingrate von{' '}
              {format(carData?.rate || 0)} wurde auf {format(vehicleRate || 0)}{' '}
              aktualisiert. Du kannst deinen Leasingantrag mit der
              aktualisierten Rate fortsetzen oder den Leasingantrag stornieren.
            </p>
          </div>
          <ButtonGroup
            type='default'
            className='top-24 w-auto!'
            buttonOneProps={{
              children: 'Mit dem aktualisierten Angebot fortfahren',
              type: 'button',
              onClick: () => {
                ls.set('pcms', vehicleRate);
                setPriceChangeModalVisibility(false);
              },
            }}
            buttonTwoProps={{
              variant: 'secondary-outlined',
              StartIcon: CloseCircleIcon,
              children: 'Leasingantrag stornieren',
              type: 'button',
              onClick: () => {
                cancelLeaseApp();
              },
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default SubHeader;
